import React, { useEffect, useRef, useState } from 'react';
import { api, personnelApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Search, FileSpreadsheet, Upload, FileText, Pencil, UserPlus, Trash2, X, Link2,
} from 'lucide-react';
import { roleTr, departmentTr, rolesForDepartment, PERSONNEL_ROLES } from '../../lib/roles';
import PersonnelDocumentsModal from './PersonnelDocumentsModal';
import CreateUserForPersonnelModal from './CreateUserForPersonnelModal';

const PersonnelPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const canManage = ['super_admin', 'admin', 'supervisor', 'ik'].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [unvan, setUnvan] = useState('');

  const [docsFor, setDocsFor] = useState(null);
  const [userFor, setUserFor] = useState(null);
  const [editing, setEditing] = useState(null); // {} for new, object for edit
  const [importing, setImporting] = useState(false);
  const [matchModal, setMatchModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRole, setBulkRole] = useState('');
  const [bulkDept, setBulkDept] = useState('');
  const importInput = useRef(null);

  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleSelectAll = () => setSelectedIds((s) => (s.length === rows.length ? [] : rows.map((r) => r.id)));

  const applyBulkRole = async () => {
    if (!bulkRole || !selectedIds.length) return;
    try {
      const res = await personnelApi.bulkRole({ personnel_ids: selectedIds, role: bulkRole, department: bulkDept || undefined });
      const d = res.data?.data || {};
      toast({
        title: 'Toplu rol güncellendi',
        description: `${d.updated} güncellendi${d.no_user ? ` · ${d.no_user} kişinin hesabı yok (atlandı)` : ''}`,
        variant: 'success',
      });
      setSelectedIds([]); setBulkRole(''); setBulkDept('');
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.message || 'Güncellenemedi', variant: 'destructive' });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (department) params.department = department;
      if (unvan) params.unvan = unvan;
      const res = await personnelApi.list(params);
      setRows(res.data?.data || []);
    } catch {
      toast({ title: 'Hata', description: 'Personel listesi alınamadı', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, department, unvan]);
  useEffect(() => { api.get('/teams').then((r) => setTeams(r.data?.data || [])).catch(() => {}); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/personnel/io/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'personel-sablonu.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Hata', description: 'Şablon indirilemedi', variant: 'destructive' });
    }
  };

  const downloadCredentialsCsv = (users) => {
    const header = ['Ad Soyad', 'Sicil No', 'E-posta', 'Şifre', 'Rol'];
    const rows = users.map((u) => [u.ad_soyad, u.sicil_no, u.email, u.sifre, u.rol]);
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '﻿' + [header, ...rows].map((r) => r.map(esc).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `personel-kullanici-sifre-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await personnelApi.import(fd);
      const d = res.data?.data || {};
      const userCount = d.users?.length || 0;
      toast({
        title: 'İçe aktarıldı',
        description: `${d.created} yeni, ${d.updated} güncellendi, ${d.skipped} atlandı${userCount ? ` · ${userCount} kullanıcı oluşturuldu` : ''}`,
        variant: 'success',
      });
      if (userCount) downloadCredentialsCsv(d.users);
      if (d.userErrors?.length) {
        toast({ title: 'Bazı kullanıcılar oluşturulamadı', description: `${d.userErrors.length} kayıtta hata`, variant: 'destructive' });
      }
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.message || 'İçe aktarılamadı', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (p) => {
    const alsoUser = p.has_user && window.confirm('Bağlı kullanıcı hesabı da silinsin mi? (İptal = sadece personel kaydı)');
    if (!window.confirm(`${p.first_name} ${p.last_name} silinsin mi?`)) return;
    try {
      await personnelApi.remove(p.id, alsoUser);
      toast({ title: 'Silindi', variant: 'success' });
      load();
    } catch {
      toast({ title: 'Hata', description: 'Silinemedi', variant: 'destructive' });
    }
  };

  const openMatch = async () => {
    try {
      const res = await personnelApi.matchDailyAssignments();
      setMatchModal(res.data?.data);
    } catch {
      toast({ title: 'Hata', description: 'Eşleştirme alınamadı', variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Personeller</h1>
        <p className="text-sm text-slate-500 mt-1">
          Personeli unvanına göre yönetin, ekiplere yerleştirin ve günlük atama yapın
        </p>
      </div>

      {/* Araç çubuğu */}
      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Personel ara (ad, sicil, unvan)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <select value={unvan} onChange={(e) => setUnvan(e.target.value)} className="h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
          <option value="">Tüm Unvanlar</option>
          {[...new Set(rows.map((r) => r.unvan).filter(Boolean))].map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={department} onChange={(e) => setDepartment(e.target.value)} className="h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
          <option value="">Tüm Departmanlar</option>
          <option value="saha">Saha</option>
          <option value="idari">İdari</option>
        </select>
        {canManage && (
          <>
            <Button variant="outline" className="h-10" onClick={openMatch}>
              <Link2 className="h-4 w-4 mr-2" /> Atama Eşleştir
            </Button>
            <Button variant="outline" className="h-10" onClick={downloadTemplate}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel Şablonu
            </Button>
            <input ref={importInput} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => { handleImport(e.target.files?.[0]); e.target.value = ''; }} />
            <Button className="h-10 bg-blue-600 hover:bg-blue-700" disabled={importing} onClick={() => importInput.current?.click()}>
              <Upload className="h-4 w-4 mr-2" /> {importing ? 'Yükleniyor...' : 'Excel ile Yükle'}
            </Button>
            <Button variant="outline" className="h-10" onClick={() => setEditing({})}>
              <UserPlus className="h-4 w-4 mr-2" /> Yeni
            </Button>
          </>
        )}
      </div>

      {/* Toplu işlem çubuğu */}
      {canManage && selectedIds.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <span className="text-sm font-medium text-blue-800">{selectedIds.length} personel seçili</span>
          <div className="flex flex-wrap items-center gap-2">
            <select value={bulkDept} onChange={(e) => setBulkDept(e.target.value)} className="h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white">
              <option value="">Departman (değiştirme)</option>
              <option value="saha">Saha</option>
              <option value="idari">İdari</option>
            </select>
            <select value={bulkRole} onChange={(e) => setBulkRole(e.target.value)} className="h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white">
              <option value="">Rol seçin</option>
              {PERSONNEL_ROLES.map((r) => <option key={r} value={r}>{roleTr(r)}</option>)}
            </select>
            <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" disabled={!bulkRole} onClick={applyBulkRole}>
              Rolü Uygula
            </Button>
            <Button size="sm" variant="outline" className="h-9" onClick={() => setSelectedIds([])}>Temizle</Button>
          </div>
          <span className="text-[11px] text-blue-600 sm:ml-auto">Not: Yalnızca hesabı olan personelin rolü değişir.</span>
        </div>
      )}

      {/* Tablo */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                {canManage && (
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleSelectAll} />
                  </th>
                )}
                <th className="px-4 py-3 font-medium">Sicil</th>
                <th className="px-4 py-3 font-medium">Ad Soyad</th>
                <th className="px-4 py-3 font-medium">Unvan</th>
                <th className="px-4 py-3 font-medium">Departman</th>
                <th className="px-4 py-3 font-medium">Rol</th>
                <th className="px-4 py-3 font-medium">Ekip</th>
                <th className="px-4 py-3 font-medium text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-10 text-center text-slate-400">Kayıt bulunamadı</td></tr>
              ) : rows.map((p) => (
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedIds.includes(p.id) ? 'bg-blue-50/50' : ''}`}>
                  {canManage && (
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)} />
                    </td>
                  )}
                  <td className="px-4 py-3 text-slate-500">{p.sicil_no || '—'}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{p.first_name} {p.last_name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.unvan || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{departmentTr(p.department) || '—'}</td>
                  <td className="px-4 py-3">
                    {p.user_role ? (
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                        {roleTr(p.user_role)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Hesap yok</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {p.team_code ? `${p.team_code}` : <span className="italic text-slate-400">Atanmamış</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="outline" size="sm" className="h-8" onClick={() => setDocsFor(p)}>
                        <FileText className="h-3.5 w-3.5 mr-1.5" /> Belgeler
                      </Button>
                      {canManage && !p.has_user && (
                        <Button variant="outline" size="sm" className="h-8 text-blue-600" onClick={() => setUserFor(p)}>
                          <UserPlus className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canManage && (
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditing(p)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canManage && (
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-red-500" onClick={() => handleDelete(p)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {docsFor && <PersonnelDocumentsModal personnel={docsFor} onClose={() => setDocsFor(null)} onChanged={load} />}
      {userFor && <CreateUserForPersonnelModal personnel={userFor} onClose={() => setUserFor(null)} onSuccess={load} />}
      {editing && (
        <PersonnelFormModal
          personnel={editing.id ? editing : null}
          teams={teams}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {matchModal && <MatchModal data={matchModal} onClose={() => setMatchModal(null)} />}
    </div>
  );
};

/* ── Personel Ekle/Düzenle ── */
const PersonnelFormModal = ({ personnel, teams, onClose, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    sicil_no: personnel?.sicil_no || '',
    first_name: personnel?.first_name || '',
    last_name: personnel?.last_name || '',
    unvan: personnel?.unvan || '',
    department: personnel?.department || '',
    team_id: personnel?.team_id || '',
    phone: personnel?.phone || '',
    tc_no: personnel?.tc_no || '',
    role: personnel?.user_role || '',
  });
  const [loading, setLoading] = useState(false);
  const hasUser = !!personnel?.user_id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) {
      toast({ title: 'Hata', description: 'Ad ve soyad zorunlu', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, team_id: form.team_id || null, department: form.department || null };
      // Rol yalnızca bağlı kullanıcı varsa gönderilir (kullanıcının rolünü günceller)
      if (!hasUser) delete payload.role;
      if (personnel?.id) await personnelApi.update(personnel.id, payload);
      else await personnelApi.create(payload);
      toast({ title: 'Kaydedildi', variant: 'success' });
      onSaved();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.message || 'Kaydedilemedi', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">{personnel ? 'Personel Düzenle' : 'Yeni Personel'}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <Field label="Sicil No"><Input value={form.sicil_no} onChange={(e) => setForm({ ...form, sicil_no: e.target.value })} /></Field>
          <Field label="Unvan"><Input value={form.unvan} onChange={(e) => setForm({ ...form, unvan: e.target.value })} /></Field>
          <Field label="Ad *"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
          <Field label="Soyad *"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
          <Field label="Departman">
            <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
              <option value="">—</option><option value="saha">Saha</option><option value="idari">İdari</option>
            </select>
          </Field>
          <Field label="Ekip">
            <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
              <option value="">Atanmamış</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
            </select>
          </Field>
          <Field label="Telefon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="TC No"><Input value={form.tc_no} onChange={(e) => setForm({ ...form, tc_no: e.target.value })} /></Field>
          {hasUser && (
            <Field label="Rol (kullanıcı hesabı)">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
                <option value="">Değiştirme</option>
                {rolesForDepartment(form.department).map((r) => <option key={r} value={r}>{roleTr(r)}</option>)}
              </select>
            </Field>
          )}
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">{loading ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-slate-700">{label}</Label>
    {children}
  </div>
);

/* ── Günlük atama eşleştirme görünümü ── */
const MatchModal = ({ data, onClose }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-slate-900">Günlük Atama Eşleştirme</h2>
        <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
      </div>
      <div className="p-6 overflow-y-auto space-y-4">
        <p className="text-sm text-slate-500">
          Günlük atamalarda toplam <b>{data.total_names}</b> farklı isim var.
          <span className="text-emerald-600"> {data.matched.length} eşleşti</span>,
          <span className="text-amber-600"> {data.unmatched.length} eşleşmedi</span>.
        </p>
        {data.unmatched.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase text-amber-600 mb-2">Eşleşmeyen isimler (personel kaydı yok)</p>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 max-h-60 overflow-y-auto space-y-1">
              {data.unmatched.map((n) => <p key={n} className="text-sm text-slate-700">{n}</p>)}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Bu isimleri Excel ile içe aktarın veya "Yeni" ile ekleyin; ardından kullanıcı oluşturun.
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default PersonnelPage;
